import React from 'react';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import Button from 'components/UI/V3/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import styles from './EvaluationQuestions.module.scss';
import { EvaluationQuestion } from 'model/Service';
import { FieldError } from '@spike/model';
import { getErrorMessage, getQuestionError } from './Validations';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';

interface EvaluationQuestionsProps {
    evaluationQuestionsEnabled: boolean;
    evaluationQuestions: EvaluationQuestion[];
    errors: Array<FieldError>;
    touchedQuestions: Set<number | string>;
    attemptedSave: boolean;
    onToggleCustomQuestions: (enabled: boolean) => void;
    onUpdateQuestion: (questionId: number | string, field: keyof EvaluationQuestion, value: any) => void;
    onRemoveQuestion: (questionId: number | string) => void;
    onAddQuestion: () => void;
}

export const EvaluationQuestions: React.FC<EvaluationQuestionsProps> = ({
    evaluationQuestionsEnabled,
    evaluationQuestions,
    errors,
    touchedQuestions,
    attemptedSave,
    onToggleCustomQuestions,
    onUpdateQuestion,
    onRemoveQuestion,
    onAddQuestion
}) => {
    const getInlineError = (text: string): string => {
        if (!text.trim()) return 'Question text is required';
        if (text.length > 255) return 'Question must be less than 255 characters';
        return '';
    };

    return (
        <div className={styles.section}>
            {/* Header with Toggle */}
            <div className={styles.headerSection}>
                <div className={styles.headerContent}>
                    <Label error={getErrorMessage(errors, 'questions')}>Custom Questions</Label>
                    <Switch
                        checked={evaluationQuestionsEnabled}
                        onChange={onToggleCustomQuestions}
                    />
                </div>
                <p className={styles.description}>Add your own eval questions to record pass/fail results</p>
            </div>

            {/* Questions List - Only show when enabled */}
            {evaluationQuestionsEnabled && (
                <div className={styles.questionsContainer}>
                    {evaluationQuestions.map((question, index) => {
                        const questionId = question.id || question.displayOrder || index;
                        const parentError = getQuestionError(errors, index);
                        const inlineError = getInlineError(question.questionText);
                        const isExistingQuestion = !!question.id;
                        const isTouched = touchedQuestions.has(questionId);

                        // Show errors similar to CustomQuestions:
                        // - existing questions: immediately when touched
                        // - new questions: only after attempted save
                        const shouldShowError =
                            ((isExistingQuestion && isTouched) || (!isExistingQuestion && attemptedSave)) &&
                            inlineError !== '';

                        const labelText = shouldShowError ? inlineError : parentError || 'Question';

                        return (
                            <div
                                key={questionId}
                                className={styles.questionCard}
                            >
                                {/* Question Header */}
                                <div className={styles.questionHeader}>
                                    <button
                                        type="button"
                                        className={styles.deleteButton}
                                        onClick={() => onRemoveQuestion(questionId)}
                                        aria-label="Delete question"
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>

                                {/* Question Text */}
                                <Textarea
                                    label="Question"
                                    error={shouldShowError || parentError ? 'Question text is required' : null}
                                    value={question.questionText}
                                    onChange={e => onUpdateQuestion(questionId, 'questionText', e.currentTarget.value)}
                                    rows={3}
                                    maxLength={1000}
                                    placeholder="Enter your question here..."
                                    className={styles.questionTextarea}
                                />

                                {/* Preview Response */}
                                <div className={styles.responseSection}>
                                    <Label className={styles.responseLabel}>Preview response</Label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioOption}>
                                            <input
                                                type="radio"
                                                className={styles.radioInput}
                                                disabled={true}
                                            />
                                            <span className={styles.radioLabel}>Pass</span>
                                        </label>
                                        <label className={styles.radioOption}>
                                            <input
                                                type="radio"
                                                className={styles.radioInput}
                                                disabled={true}
                                            />
                                            <span className={styles.radioLabel}>Fail</span>
                                        </label>
                                        <label className={styles.radioOption}>
                                            <input
                                                type="radio"
                                                className={styles.radioInput}
                                                disabled={true}
                                            />
                                            <span className={styles.radioLabel}>Conditional Pass</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Add Question Button */}
                    <Button
                        label="Add Question"
                        size="inline"
                        variant="text-green"
                        onClick={onAddQuestion}
                        leftIcon={faPlus}
                        className={styles.addButton}
                    />
                </div>
            )}
        </div>
    );
};

export default EvaluationQuestions;
